import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { buildEndPoint, mapFiltersToOdata } from '../../utils/odataEndPoint';
import { sortDirections } from '../../types/lists/listTypes';
import { useNotification } from '../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../utils/commonVariables/messages';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ODataQuery {
    entityName: string;
    query: {
        select?: string[];
        expand?: string[];
        filter?: ODataFilter[];
        orderBy?: string;
        top?: number;
    };
}

export type ODataFilter =
    | string
    | { and?: ODataFilter[] }
    | { or?: ODataFilter[] };

export interface UseListContextOptions<TRaw, TState, TFilters> {
    initialFilters: TFilters;
    buildQuery: (
        filtersToOdata: ODataFilter[],
        sortedBy: string,
        sortDir: sortDirections
    ) => ODataQuery;
    adaptItem: (raw: TRaw) => TState;
    initialSortBy?: string;
    initialSortDir?: sortDirections;
    itemsPerPage?: number;
    dependencies?: unknown[];
    skipInitialFetch?: boolean;
    loadPage?: (
        query: ODataQuery,
        page: number
    ) => Promise<{ items: TRaw[]; total: number }>;
    errorMessage?: FeedbackMessages;
}

export interface UseListContextReturn<TState, TFilters> {
    items: TState[];
    filters: TFilters;
    handleFilterChange: (key: keyof TFilters, value: any) => void;
    resetFilters: () => void;
    sortDir: sortDirections;
    sortedBy: string;
    sortByField: (field: string) => void;
    handleSearch: (pageNumber?: number) => void;
    handleClearFilters: () => void;
    handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
    isLoading: boolean;
    totalResults: number;
    currentResults: number;
    page: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useListContext = <TRaw, TState, TFilters>({
    initialFilters,
    buildQuery,
    adaptItem,
    initialSortBy = 'Id',
    initialSortDir = sortDirections.DESC,
    itemsPerPage = 40,
    dependencies = [],
    skipInitialFetch = false,
    loadPage,
    errorMessage = FeedbackMessages.ERROR_FETCHING,
}: UseListContextOptions<TRaw, TState, TFilters>): UseListContextReturn<
    TState,
    TFilters
> => {
    const [isLoading, setIsLoading] = useState(false);
    const { setErrorMessage } = useNotification();
    const [items, setItems] = useState<TState[]>([]);
    const [filters, setFilters] = useState<TFilters>(initialFilters);
    const [sortedBy, setSortedBy] = useState(initialSortBy);
    const [sortDir, setSortDir] = useState<sortDirections>(initialSortDir);
    const [page, setPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);

    const totalPages = Math.ceil(totalResults / itemsPerPage);

    // ── Core fetch ────────────────────────────────────────────────────────────

    const fetchData = useCallback(
        async (pageNumber = 1, filtersState: TFilters = filters) => {
            // Defensive normalization in case handlers pass an event object.
            const normalizedPage = Number(pageNumber);
            const nextPage =
                Number.isFinite(normalizedPage) && normalizedPage > 0
                    ? Math.floor(normalizedPage)
                    : 1;

            setIsLoading(true);
            setPage(nextPage);
            if (nextPage === 1) setItems([]);

            const filtersToOdata = mapFiltersToOdata(filtersState);
            const odataQuery = buildQuery(filtersToOdata, sortedBy, sortDir);
            const endpoint = buildEndPoint(odataQuery, nextPage);
            try {
                const result = loadPage
                    ? await loadPage(odataQuery, nextPage)
                    : await api.get(endpoint).then(({ data }) => ({
                          items: data.value as TRaw[],
                          total: parseInt(data['odata.count']),
                      }));
                const _total = result.total;
                setTotalResults(_total);

                const newItems: TState[] = result.items.map(adaptItem);
                if (nextPage === 1) {
                    setItems(newItems);
                } else {
                    setItems(prev => [...prev, ...newItems]);
                }
            } catch {
                setErrorMessage(errorMessage);
            } finally {
                setIsLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sortedBy, sortDir, filters, ...dependencies]
    );

    // Re-fetch when sort or external deps change
    useEffect(() => {
        if (!skipInitialFetch) fetchData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedBy, sortDir, ...dependencies]);

    // ── Public handlers ───────────────────────────────────────────────────────

    const handleFilterChange = (key: keyof TFilters, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: { ...prev[key], value },
        }));
    };

    const handleResetFilters = () => {
        setFilters(initialFilters);
        fetchData(1, initialFilters);
    };

    const sortByField = (field: string) => {
        if (field === sortedBy) {
            setSortDir(prev =>
                prev === sortDirections.ASC
                    ? sortDirections.DESC
                    : sortDirections.ASC
            );
        } else {
            setSortedBy(field);
            setSortDir(sortDirections.ASC);
        }
    };

    const handleSearch = (pageNumber = 1) => {
        const normalizedPage = Number(pageNumber);
        const nextPage =
            Number.isFinite(normalizedPage) && normalizedPage > 0
                ? Math.floor(normalizedPage)
                : 1;
        fetchData(nextPage);
    };

    const handleClearFilters = () => {
        setFilters(initialFilters);
        fetchData(1, initialFilters);
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isOnBottom = scrollTop >= scrollHeight - clientHeight - 1;
        if (isOnBottom && !isLoading && page < totalPages) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchData(nextPage);
        }
    };

    return {
        items,
        filters,
        handleFilterChange,
        sortDir,
        sortedBy,
        sortByField,
        handleSearch,
        handleClearFilters,
        handleScroll,
        isLoading,
        totalResults,
        currentResults: items.length,
        page,
        resetFilters: handleResetFilters,
    };
};
